import { Component, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import i18n from './i18n';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NetworkProvider, useNetwork } from './context/NetworkContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { SocketProvider } from './context/SocketContext';
import { ToastProvider } from './context/ToastContext';
import { AlertProvider } from './hooks/useAlert';
import { setupForegroundNotificationHandler } from './services/webPush';
import { trackEvent } from './services/analytics';

import Splash from './components/Splash';
import BottomTabBar from './components/BottomTabBar';
import BannedPopup from './components/BannedPopup';
import NoInternet from './components/NoInternet';
import TutorialProvider from './tutorial/TutorialProvider';

import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Onboarding from './pages/Onboarding';
import Language, { languageChosen } from './pages/Language';
import GuestLanding from './pages/GuestLanding';
import GuestBar from './components/GuestBar';
import { GuestGateProvider } from './context/GuestGateContext';
import InstallPrompt from './pages/InstallPrompt';
import { detectInstallContext, hasSeenInstallPrompt, shouldGateOnInstall } from './hooks/useInstallEligibility';
import Home from './pages/Home';
import Events from './pages/Events';
import Leaderboard from './pages/Leaderboard';
import EventPopup from './components/EventPopup';
import HappyHourPopup from './components/HappyHourPopup';
import FirstDepositCelebrationPopup from './components/FirstDepositCelebrationPopup';
import Games from './pages/Games';
import TopUp from './pages/TopUp';
import WalletTopUp from './pages/WalletTopUp';
import Withdraw from './pages/Withdraw';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import NotificationPreferences from './pages/NotificationPreferences';
import OrderTracking from './pages/OrderTracking';
import FAQ from './pages/FAQ';
import Help from './pages/Help';

class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: '#0f172a',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28,
        }}>
          <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b', fontSize: 40 }} />
          <div style={{ color: '#f8fafc', fontSize: 18, fontWeight: 800, marginTop: 16, textAlign: 'center' }}>
            {i18n.t('errorBoundary.title')}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 8, textAlign: 'center' }}>
            {i18n.t('errorBoundary.message')}
          </div>
          <button
            onClick={() => location.reload()}
            style={{
              marginTop: 24,
              backgroundColor: '#14f1d1', color: '#020617',
              border: 'none', padding: '10px 24px', borderRadius: 999,
              fontSize: 14, fontWeight: 800, cursor: 'pointer',
            }}
          >
            {i18n.t('errorBoundary.reload')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AuthFlow() {
  const location = useLocation();
  const navigate = useNavigate();

  // First-time iOS visitors get gated on the install prompt before anything
  // else. Then a fresh visitor picks a language, and only after that lands on
  // onboarding (so the slides + everything after render in their choice).
  useEffect(() => {
    const allowed = ['/install', '/language', '/onboarding', '/login', '/register'];
    if (allowed.includes(location.pathname)) return;

    const ctx = detectInstallContext();
    if (shouldGateOnInstall(ctx) && !hasSeenInstallPrompt()) {
      navigate('/install', { replace: true });
    } else if (!languageChosen()) {
      navigate('/language', { replace: true });
    } else {
      navigate('/onboarding', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/install" element={<InstallPrompt />} />
      <Route path="/language" element={<Language />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="*" element={<Onboarding />} />
    </Routes>
  );
}

function MainRoutes() {
  const { colors } = useTheme();
  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg, color: colors.text, paddingBottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' }}>
      <Routes>
        <Route path="/home" element={<Home />} />
        <Route path="/events" element={<Events />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/games" element={<Games />} />
        <Route path="/topup" element={<TopUp />} />
        <Route path="/wallet-topup" element={<WalletTopUp />} />
        <Route path="/withdraw" element={<Withdraw />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/notification-prefs" element={<NotificationPreferences />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/help" element={<Help />} />
        <Route path="/order/:type/:id" element={<OrderTracking />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
      <BottomTabBar />
      <TutorialProvider />
      <EventPopup />
      <HappyHourPopup />
      <FirstDepositCelebrationPopup />
    </div>
  );
}

// Browse-before-login: guests reach the preview landing + games, and can open
// the real feature pages, but any account action is gated to Login/Register.
function GuestRoutes() {
  const { colors } = useTheme();
  const location = useLocation();
  const onAuthPage = ['/login', '/register', '/forgot-password'].includes(location.pathname);
  return (
    <GuestGateProvider>
      <div style={{ minHeight: '100dvh', backgroundColor: colors.bg, color: colors.text, paddingBottom: onAuthPage ? 0 : 'calc(64px + env(safe-area-inset-bottom, 0px))' }}>
        <Routes>
          <Route path="/browse" element={<GuestLanding />} />
          <Route path="/games" element={<Games />} />
          <Route path="/topup" element={<TopUp />} />
          <Route path="/wallet-topup" element={<WalletTopUp />} />
          <Route path="/withdraw" element={<Withdraw />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="*" element={<Navigate to="/browse" replace />} />
        </Routes>
        {!onAuthPage && <GuestBar />}
      </div>
    </GuestGateProvider>
  );
}

function AppShell() {
  const { isLoggedIn, isGuest, isBanned, dismissBanned } = useAuth();
  const { isConnected } = useNetwork();
  const location = useLocation();

  useEffect(() => {
    if (!isLoggedIn) return;
    const unsubscribe = setupForegroundNotificationHandler();
    return unsubscribe;
  }, [isLoggedIn]);

  useEffect(() => {
    trackEvent('page_view', {
      page_path: location.pathname,
      page_location: window.location.href,
    });
  }, [location.pathname]);

  if (isLoggedIn === null) return <Splash />;

  return (
    <>
      {isLoggedIn ? <MainRoutes /> : isGuest ? <GuestRoutes /> : <AuthFlow />}
      {isBanned && <BannedPopup onDismiss={dismissBanned} />}
      {isConnected === false && <NoInternet />}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NetworkProvider>
          <ThemeProvider>
            <AuthProvider>
              <LanguageProvider>
                <SocketProvider>
                  <ToastProvider>
                    <AlertProvider>
                      <AppShell />
                    </AlertProvider>
                  </ToastProvider>
                </SocketProvider>
              </LanguageProvider>
            </AuthProvider>
          </ThemeProvider>
        </NetworkProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
